import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

// English ships in the initial bundle (fallbackLng + most-likely first paint).
// Other locales lazy-load via dynamic import — keeps the entry chunk small.
import en from './locales/en.json';
import { SUPPORTED_LOCALES, useLanguageStore } from '@/stores/language';

/**
 * Fold a browser language tag onto one of the locales we actually ship.
 *
 * We carry zh-CN and zh-TW, but a Hong Kong browser sends zh-HK and a
 * Singaporean one sends zh-SG — neither is in supportedLngs, so both used to
 * land on English. For a Chinese-astrology app that is the worst possible
 * fallback, so map the regional tags onto the right script.
 */
const ZH_TRADITIONAL = /^zh([-_](hant|tw|hk|mo))/i;
const ZH_SIMPLIFIED = /^zh([-_](hans|cn|sg|my))?$/i;

export function normalizeLocale(lng: string): string {
  if (ZH_TRADITIONAL.test(lng)) return 'zh-TW';
  if (ZH_SIMPLIFIED.test(lng)) return 'zh-CN';
  // pt-PT and friends: keep the base match our catalogue can serve.
  if (/^pt([-_]|$)/i.test(lng)) return 'pt-BR';
  return lng;
}

const lazyLoaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  id: () => import('./locales/id.json'),
  ja: () => import('./locales/ja.json'),
  ko: () => import('./locales/ko.json'),
  ms: () => import('./locales/ms.json'),
  'pt-BR': () => import('./locales/pt-BR.json'),
  th: () => import('./locales/th.json'),
  vi: () => import('./locales/vi.json'),
  'zh-CN': () => import('./locales/zh-CN.json'),
  'zh-TW': () => import('./locales/zh-TW.json'),
};

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en } },
    supportedLngs: [...SUPPORTED_LOCALES],
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    detection: {
      order: ['querystring', 'cookie', 'localStorage', 'navigator', 'htmlTag'],
      caches: ['cookie', 'localStorage'],
      lookupCookie: 'bz-i18n',
      lookupLocalStorage: 'bz-i18n',
      convertDetectedLanguage: normalizeLocale,
    },
    // Locales load lazily, so components mounted before the bundle arrives
    // must re-render on 'added' — 'languageChanged' alone never fires for them.
    react: { useSuspense: false, bindI18nStore: 'added' },
    partialBundledLanguages: true,
  });

async function ensureLanguageLoaded(lng: string): Promise<void> {
  if (i18n.hasResourceBundle(lng, 'translation')) return;
  const loader = lazyLoaders[lng];
  if (!loader) return;
  const mod = await loader();
  i18n.addResourceBundle(lng, 'translation', mod.default, true, true);
}

/**
 * How long the first paint may wait for the starting locale's bundle.
 *
 * The bundle is a separate chunk over the network. Waiting for it unbounded
 * means a stalled request holds render hostage forever and the user stares at
 * an empty #root — strictly worse than showing them a working English UI.
 */
const FIRST_PAINT_LOCALE_BUDGET_MS = 3000;

async function applyLocale(lng: string): Promise<void> {
  await ensureLanguageLoaded(lng);
  if (i18n.language !== lng) {
    await i18n.changeLanguage(lng);
    return;
  }
  // The detector already set this language, so changeLanguage short-circuits
  // and emits nothing. The resources, however, only just arrived — so anything
  // that rendered before them (the skip link, the shell) is still showing the
  // English fallback and has no event coming to correct it. Announce it.
  i18n.emit('languageChanged', lng);
}

/**
 * Settles when the starting locale is ready — or when the budget above runs
 * out, whichever comes first.
 *
 * main.tsx waits on this before the first render, because loading the bundle
 * afterwards is not enough on its own: both 'added' and 'languageChanged' can
 * fire before React has mounted and subscribed, which used to leave whatever
 * rendered first (the skip link, the shell) stuck on English for good.
 *
 * The load is NOT cancelled when the budget expires — it keeps going and swaps
 * the UI over via applyLocale if it lands late. So a slow network costs a brief
 * flash of English instead of a blank page, and a dead network still yields a
 * usable app.
 */
export const i18nReady: Promise<void> = (async () => {
  // A persisted choice wins over whatever the detector guessed.
  const stored = useLanguageStore.getState().language;
  const target = stored || i18n.language;
  if (!target || target === 'en') return;

  const loading = applyLocale(target).catch((err: unknown) => {
    // A missing or corrupt chunk must not block boot — English is the fallback.
    console.error(`[i18n] could not load locale "${target}"`, err);
  });

  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    loading.finally(() => clearTimeout(timer)),
    new Promise<void>((resolve) => {
      timer = setTimeout(resolve, FIRST_PAINT_LOCALE_BUDGET_MS);
    }),
  ]);
})();

// The detector settles the language during init(), before the listener below
// exists — so the first value has to be applied by hand or <html lang> keeps
// saying "en" for every non-English visitor.
if (typeof document !== 'undefined' && i18n.language) {
  document.documentElement.lang = i18n.language;
}

// Mirror i18next changes into the Zustand store + <html lang>.
i18n.on('languageChanged', (lng) => {
  if (typeof document !== 'undefined') document.documentElement.lang = lng;
  useLanguageStore.getState().setLanguage(lng as (typeof SUPPORTED_LOCALES)[number]);
});

// Public helper for the language picker (Phase 2 settings screen).
export async function changeLanguage(lng: string): Promise<void> {
  await ensureLanguageLoaded(lng);
  await i18n.changeLanguage(lng);
}

export default i18n;
